import { ServiceLayer } from '@packages/cms-components';
import { Box, Dialog, Image, Stack, Typography } from '@packages/shared';
import { useState } from 'react';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';
import { useLayerTracking } from '../Tracking/hooks/useLayerTracking';
import { useServiceLayerTracking } from '../Tracking/hooks/useServiceLayerTracking';

/*
 * INFO: As of right now the data structure for productHints and legalHints is the same, so the components are mostly identical.
 * The data for these two might change in the future and then the two components would have to be split again. For this reason we decided to split into seperate cards them from the start.
 */

const LegalHintsItemFragment = graphql(/* GraphQL */ `
  fragment LegalHintsItemFragment on Hint {
    iconUrl
    text
    serviceLayerId
    dialogContent
  }
`);

export type LegalHintsItemProps = {
  legalHintsData: FragmentType<typeof LegalHintsItemFragment>;
};

/**
 * LegalHintsItem component displays a legal hint
 * */
export const LegalHintsItem = ({ legalHintsData }: LegalHintsItemProps) => {
  const { iconUrl, serviceLayerId, text, dialogContent } = getFragmentData(
    LegalHintsItemFragment,
    legalHintsData,
  );

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { trackViewLayer } = useLayerTracking();
  const { trackServiceLayerClick } = useServiceLayerTracking();

  const handleClick = () => {
    trackViewLayer(text);
    trackServiceLayerClick(serviceLayerId!);

    setIsDialogOpen(true);
  };

  return (
    <>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        {...((serviceLayerId || dialogContent) && {
          onClick: handleClick,
          role: 'button',
          sx: { cursor: 'pointer' },
        })}
      >
        <Image src={iconUrl} alt={text} height={24} width={24} />
        <Typography variant="body2">{text}</Typography>
      </Stack>
      {serviceLayerId !== null && (
        <Dialog
          title={text}
          open={isDialogOpen}
          dialogContent={
            dialogContent ? (
              <Box dangerouslySetInnerHTML={{ __html: dialogContent }} p={2} />
            ) : (
              <ServiceLayer serviceLayerId={serviceLayerId} />
            )
          }
          handleClose={() => setIsDialogOpen(false)}
          displayTrackingProps={{ category: 'dv-service-layer-legal-hints-item' }}
        />
      )}
    </>
  );
};
